import type {
  OfflineMissionBundle,
  OfflineMissionMutation,
  OfflineMissionStore,
} from "./offline-missions";
import {
  IndexedDbOfflineCryptoKeyProvider,
  OfflineLocalCipher,
  type EncryptedOfflineBytes,
} from "./offline-missions-crypto.ts";

type StoredMissionBundle = {
  storageKey: string;
  missionId: string;
  profileId: string;
  workDate: string;
  startsAt: string;
  encryptedPayload: EncryptedOfflineBytes;
};

type StoredMutation = {
  idempotencyKey: string;
  missionId: string;
  profileId: string;
  createdAt: string;
  status: OfflineMissionMutation["status"];
  encryptedPayload: EncryptedOfflineBytes;
};

type StoredEvidenceFile = {
  evidenceId: string;
  profileId: string;
  missionId: string;
  encryptedMetadata: EncryptedOfflineBytes;
  encryptedBlob: EncryptedOfflineBytes;
};

export type OfflineEvidenceFile = {
  evidenceId: string;
  profileId: string;
  missionId: string;
  blob: Blob;
  mimeType: string;
  originalName: string;
  capturedAt: string;
};

const DATABASE_NAME = "ard-manager-offline";
const DATABASE_VERSION = 3;

function missionStorageKey(mission: OfflineMissionBundle) {
  return `${mission.profileId}:${mission.workDate}:${mission.missionId}`;
}

function requestResult<T>(request: IDBRequest<T>) {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB indisponible."));
  });
}

function transactionDone(transaction: IDBTransaction) {
  return new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("Écriture locale impossible."));
    transaction.onabort = () => reject(transaction.error ?? new Error("Écriture locale annulée."));
  });
}

export class IndexedDbOfflineMissionStore implements OfflineMissionStore {
  private databasePromise: Promise<IDBDatabase> | null = null;
  private readonly cipher: OfflineLocalCipher;

  constructor(cipher = new OfflineLocalCipher(new IndexedDbOfflineCryptoKeyProvider())) {
    this.cipher = cipher;
  }

  private async migrateLegacyRows(database: IDBDatabase) {
    const missionTransaction = database.transaction("missions", "readonly");
    const missionRows = await requestResult(
      missionTransaction.objectStore("missions").getAll() as IDBRequest<
        Array<StoredMissionBundle | (OfflineMissionBundle & { storageKey: string })>
      >,
    );
    const legacyMissions = missionRows.filter(
      (row): row is OfflineMissionBundle & { storageKey: string } =>
        !("encryptedPayload" in row),
    );
    if (legacyMissions.length) {
      const encrypted = await Promise.all(
        legacyMissions.map(async ({ storageKey, ...mission }) => ({
          storageKey,
          missionId: mission.missionId,
          profileId: mission.profileId,
          workDate: mission.workDate,
          startsAt: mission.startsAt,
          encryptedPayload: await this.cipher.encryptJson(
            mission.profileId,
            `mission:${storageKey}`,
            mission,
          ),
        } satisfies StoredMissionBundle)),
      );
      const transaction = database.transaction("missions", "readwrite");
      encrypted.forEach((row) => transaction.objectStore("missions").put(row));
      await transactionDone(transaction);
    }

    const mutationTransaction = database.transaction("mutations", "readonly");
    const mutationRows = await requestResult(
      mutationTransaction.objectStore("mutations").getAll() as IDBRequest<
        Array<StoredMutation | OfflineMissionMutation>
      >,
    );
    const legacyMutations = mutationRows.filter(
      (row): row is OfflineMissionMutation => !("encryptedPayload" in row),
    );
    if (legacyMutations.length) {
      const encrypted = await Promise.all(
        legacyMutations.map(async (mutation) => ({
          idempotencyKey: mutation.idempotencyKey,
          missionId: mutation.missionId,
          profileId: mutation.profileId,
          createdAt: mutation.createdAt,
          status: mutation.status,
          encryptedPayload: await this.cipher.encryptJson(
            mutation.profileId,
            `mutation:${mutation.idempotencyKey}`,
            mutation,
          ),
        } satisfies StoredMutation)),
      );
      const transaction = database.transaction("mutations", "readwrite");
      encrypted.forEach((row) => transaction.objectStore("mutations").put(row));
      await transactionDone(transaction);
    }

    const evidenceTransaction = database.transaction("evidenceFiles", "readonly");
    const evidenceRows = await requestResult(
      evidenceTransaction.objectStore("evidenceFiles").getAll() as IDBRequest<
        Array<StoredEvidenceFile | OfflineEvidenceFile>
      >,
    );
    const legacyEvidence = evidenceRows.filter(
      (row): row is OfflineEvidenceFile => !("encryptedBlob" in row),
    );
    if (legacyEvidence.length) {
      const encrypted = await Promise.all(
        legacyEvidence.map(async (file) => ({
          evidenceId: file.evidenceId,
          profileId: file.profileId,
          missionId: file.missionId,
          encryptedMetadata: await this.cipher.encryptJson(
            file.profileId,
            `evidence-metadata:${file.evidenceId}`,
            {
              mimeType: file.mimeType,
              originalName: file.originalName,
              capturedAt: file.capturedAt,
            },
          ),
          encryptedBlob: await this.cipher.encryptBlob(
            file.profileId,
            `evidence-blob:${file.evidenceId}`,
            file.blob,
          ),
        } satisfies StoredEvidenceFile)),
      );
      const transaction = database.transaction("evidenceFiles", "readwrite");
      encrypted.forEach((row) => transaction.objectStore("evidenceFiles").put(row));
      await transactionDone(transaction);
    }
  }

  private openDatabase() {
    if (!this.databasePromise) {
      this.databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
        request.onupgradeneeded = () => {
          const database = request.result;
          const missions = database.objectStoreNames.contains("missions")
            ? request.transaction!.objectStore("missions")
            : database.createObjectStore("missions", { keyPath: "storageKey" });
          if (!missions.indexNames.contains("byProfileDay")) {
            missions.createIndex("byProfileDay", ["profileId", "workDate"], { unique: false });
          }
          if (!missions.indexNames.contains("byMission")) {
            missions.createIndex("byMission", "missionId", { unique: false });
          }
          const mutations = database.objectStoreNames.contains("mutations")
            ? request.transaction!.objectStore("mutations")
            : database.createObjectStore("mutations", { keyPath: "idempotencyKey" });
          if (!mutations.indexNames.contains("byMission")) {
            mutations.createIndex("byMission", "missionId", { unique: false });
          }
          if (!mutations.indexNames.contains("byProfile")) {
            mutations.createIndex("byProfile", "profileId", { unique: false });
          }
          const evidenceFiles = database.objectStoreNames.contains("evidenceFiles")
            ? request.transaction!.objectStore("evidenceFiles")
            : database.createObjectStore("evidenceFiles", { keyPath: "evidenceId" });
          if (!evidenceFiles.indexNames.contains("byProfile")) {
            evidenceFiles.createIndex("byProfile", "profileId", { unique: false });
          }
          if (!evidenceFiles.indexNames.contains("byMission")) {
            evidenceFiles.createIndex("byMission", "missionId", { unique: false });
          }
        };
        request.onsuccess = () => {
          this.migrateLegacyRows(request.result).then(
            () => resolve(request.result),
            reject,
          );
        };
        request.onerror = () => reject(request.error ?? new Error("Stockage hors ligne indisponible."));
      });
    }
    return this.databasePromise;
  }

  async saveMissionBundles(bundles: OfflineMissionBundle[]) {
    const rows = await Promise.all(
      bundles.map(async (bundle) => {
        const storageKey = missionStorageKey(bundle);
        return {
          storageKey,
          missionId: bundle.missionId,
          profileId: bundle.profileId,
          workDate: bundle.workDate,
          startsAt: bundle.startsAt,
          encryptedPayload: await this.cipher.encryptJson(
            bundle.profileId,
            `mission:${storageKey}`,
            bundle,
          ),
        } satisfies StoredMissionBundle;
      }),
    );
    const database = await this.openDatabase();
    const transaction = database.transaction("missions", "readwrite");
    const store = transaction.objectStore("missions");
    rows.forEach((row) => store.put(row));
    await transactionDone(transaction);
  }

  async getMissionBundle(missionId: string) {
    const database = await this.openDatabase();
    const transaction = database.transaction("missions", "readonly");
    const result = await requestResult(
      transaction.objectStore("missions").index("byMission").get(missionId) as IDBRequest<StoredMissionBundle | undefined>,
    );
    if (!result) return null;
    return this.cipher.decryptJson<OfflineMissionBundle>(
      result.profileId,
      `mission:${result.storageKey}`,
      result.encryptedPayload,
    );
  }

  async listMissionBundles(profileId: string, workDate: string) {
    const database = await this.openDatabase();
    const transaction = database.transaction("missions", "readonly");
    const results = await requestResult(
      transaction.objectStore("missions").index("byProfileDay").getAll([profileId, workDate]) as IDBRequest<StoredMissionBundle[]>,
    );
    const missions = await Promise.all(
      results.map((row) => this.cipher.decryptJson<OfflineMissionBundle>(
        row.profileId,
        `mission:${row.storageKey}`,
        row.encryptedPayload,
      )),
    );
    return missions.sort((left, right) => left.startsAt.localeCompare(right.startsAt));
  }

  async enqueueMutation(mutation: OfflineMissionMutation) {
    const row = {
      idempotencyKey: mutation.idempotencyKey,
      missionId: mutation.missionId,
      profileId: mutation.profileId,
      createdAt: mutation.createdAt,
      status: mutation.status,
      encryptedPayload: await this.cipher.encryptJson(
        mutation.profileId,
        `mutation:${mutation.idempotencyKey}`,
        mutation,
      ),
    } satisfies StoredMutation;
    const database = await this.openDatabase();
    const transaction = database.transaction("mutations", "readwrite");
    const store = transaction.objectStore("mutations");
    const existing = await requestResult(store.get(mutation.idempotencyKey));
    if (existing) {
      transaction.abort();
      return false;
    }
    store.add(row);
    await transactionDone(transaction);
    return true;
  }

  async listMutations(missionId?: string) {
    const database = await this.openDatabase();
    const transaction = database.transaction("mutations", "readonly");
    const store = transaction.objectStore("mutations");
    const results = missionId
      ? await requestResult(store.index("byMission").getAll(missionId) as IDBRequest<StoredMutation[]>)
      : await requestResult(store.getAll() as IDBRequest<StoredMutation[]>);
    const mutations = await Promise.all(
      results.map((row) => this.cipher.decryptJson<OfflineMissionMutation>(
        row.profileId,
        `mutation:${row.idempotencyKey}`,
        row.encryptedPayload,
      )),
    );
    return mutations.sort((left, right) => left.createdAt.localeCompare(right.createdAt));
  }

  async updateMutation(mutation: OfflineMissionMutation) {
    const row = {
      idempotencyKey: mutation.idempotencyKey,
      missionId: mutation.missionId,
      profileId: mutation.profileId,
      createdAt: mutation.createdAt,
      status: mutation.status,
      encryptedPayload: await this.cipher.encryptJson(
        mutation.profileId,
        `mutation:${mutation.idempotencyKey}`,
        mutation,
      ),
    } satisfies StoredMutation;
    const database = await this.openDatabase();
    const transaction = database.transaction("mutations", "readwrite");
    transaction.objectStore("mutations").put(row);
    await transactionDone(transaction);
  }

  async saveEvidenceFile(file: OfflineEvidenceFile) {
    if (!file.evidenceId || !file.profileId || !file.missionId || file.blob.size <= 0) {
      throw new Error("La preuve locale est incomplète.");
    }
    const row = {
      evidenceId: file.evidenceId,
      profileId: file.profileId,
      missionId: file.missionId,
      encryptedMetadata: await this.cipher.encryptJson(
        file.profileId,
        `evidence-metadata:${file.evidenceId}`,
        {
          mimeType: file.mimeType,
          originalName: file.originalName,
          capturedAt: file.capturedAt,
        },
      ),
      encryptedBlob: await this.cipher.encryptBlob(
        file.profileId,
        `evidence-blob:${file.evidenceId}`,
        file.blob,
      ),
    } satisfies StoredEvidenceFile;
    const database = await this.openDatabase();
    const transaction = database.transaction("evidenceFiles", "readwrite");
    transaction.objectStore("evidenceFiles").put(row);
    await transactionDone(transaction);
  }

  async getEvidenceFile(evidenceId: string) {
    const database = await this.openDatabase();
    const transaction = database.transaction("evidenceFiles", "readonly");
    const result = await requestResult(
      transaction.objectStore("evidenceFiles").get(evidenceId) as IDBRequest<StoredEvidenceFile | undefined>,
    );
    if (!result) return null;
    const metadata = await this.cipher.decryptJson<{
      mimeType: string;
      originalName: string;
      capturedAt: string;
    }>(
      result.profileId,
      `evidence-metadata:${result.evidenceId}`,
      result.encryptedMetadata,
    );
    return {
      evidenceId: result.evidenceId,
      profileId: result.profileId,
      missionId: result.missionId,
      ...metadata,
      blob: await this.cipher.decryptBlob(
        result.profileId,
        `evidence-blob:${result.evidenceId}`,
        result.encryptedBlob,
        metadata.mimeType,
      ),
    } satisfies OfflineEvidenceFile;
  }

  async removeEvidenceFile(evidenceId: string) {
    const database = await this.openDatabase();
    const transaction = database.transaction("evidenceFiles", "readwrite");
    transaction.objectStore("evidenceFiles").delete(evidenceId);
    await transactionDone(transaction);
  }

  async purgeProfile(profileId: string) {
    const database = await this.openDatabase();
    for (const storeName of ["missions", "mutations", "evidenceFiles"] as const) {
      const transaction = database.transaction(storeName, "readwrite");
      const store = transaction.objectStore(storeName);
      const index = store.index(storeName === "missions" ? "byProfileDay" : "byProfile");
      const range = storeName === "missions"
        ? IDBKeyRange.bound([profileId, ""], [profileId, "\uffff"])
        : IDBKeyRange.only(profileId);
      await new Promise<void>((resolve, reject) => {
        const cursorRequest = index.openKeyCursor(range);
        cursorRequest.onsuccess = () => {
          const cursor = cursorRequest.result;
          if (!cursor) {
            resolve();
            return;
          }
          store.delete(cursor.primaryKey);
          cursor.continue();
        };
        cursorRequest.onerror = () => reject(cursorRequest.error ?? new Error("Purge locale impossible."));
      });
      await transactionDone(transaction);
    }
    await this.cipher.purgeProfile(profileId);
  }
}
